import React, { useState, useEffect } from "react";
import {
    Col,
    FormGroup,
    Modal,
    ModalBody,
    Label,
    Input
} from "reactstrap";
import PropTypes from "prop-types";
import states from "../../constants/states";
import { useGetTenantMutation } from "../../utilities/mutations/useGetTenantMutation";
import PreventEnterSubmitForm from "../Forms/PreventEnterSubmitForm";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import ActionModalHeader from "../ActionModalHeader/ActionModalHeader";
import Button from "../UIButton/UIButton";
import PostalZipCodeControl from "../Addresses/PostalZipCodeControl";
import BootstrapSelect from "../BootstrapSelect/BootstrapSelect";
import "./BaseModalV2.scss";
import Spinner from "../UISpinner/UISpinner";
import provinces from "../../constants/provinces";
import { getTenantCountry } from "../../utilities/appStore";
import countries from "../../constants/countries";

const phoneUtil = PhoneNumberUtil.getInstance();



function EditTenantModal({ isOpen, cancel, accountId, submitHandler }) {
    const [data, setData] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(true);
    const [isErrored, setIsErrored] = useState(false);
    const [isValid, setIsValid] = useState(true);

    const { mutateAsync: getAsync, error: getError } = useGetTenantMutation();

    const [tenantName, setTenantName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [addressState, setAddressState] = useState(null);
    const [zipValue, setZipValue] = useState("");
    const [phoneValue, setPhoneValue] = useState("");
    const [zipError, setZipError] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const tenantCountry = getTenantCountry();
    
    useEffect(() => {
        async function load() {
            try {
                const response = await getAsync({ accountId });
                const d = response.data;

                setData(d);
                setTenantName(d.companyName || "");
                setAddress1(d.address1 || "");
                setAddress2(d.address2 || "");
                setCity(d.city || "");
                setAddressState(d.state || null);
                setZipValue(d.zip || "");
                setPhoneValue(d?.phone || "");
            } catch {
                setIsErrored(true);
            } finally {
                setIsModalLoading(false);
            }

            validateForm();
        }
        load();
    }, [accountId, getAsync]);

    // Validate form whenever relevant fields changes. Any field that affects valid state should be added here.
    useEffect(() => {
        validateForm();
    }, [tenantName, address1, city, zipError, phoneError]);

    const handleZipChange = (e, isValidZip) => {
        const val = e.target.value;
        setZipValue(val);
        setZipError(!isValidZip);
    };

    const handlePhoneChange = (e) => {
        const input = e.target.value;
        setPhoneValue(input);

        if (!/^[\d\s()+\-]*$/.test(input)) {
            setPhoneError(true);
            return;
        }

        try {
            const digitsOnly = input.replace(/\D/g, "");
            const number = phoneUtil.parse("+1" + digitsOnly);
            setPhoneError(!phoneUtil.isValidNumber(number));
        } catch {
            setPhoneError(true);
        }
    };

    const handlePhoneBlur = () => {
        try {
            const digitsOnly = phoneValue.replace(/\D/g, "");
            const number = phoneUtil.parse("+1" + digitsOnly);
            if (phoneUtil.isValidNumber(number)) {
                const formatted = phoneUtil.format(
                    number,
                    PhoneNumberFormat.NATIONAL
                );
                setPhoneValue(formatted);
            }
        } catch {
            // leave raw input
        }
    };




    const validateForm = () => {
        var valid = true;
        if (zipError === true) {
            valid = false;
        }

        if (phoneError === true) {
            valid = false
        }
        
        if (tenantName.trim().length === 0) {
            valid = false;
        }

        if (address1.trim().length === 0) {
            valid = false;
        }

        if (city.trim().length === 0) {
            valid = false;
        }

        setIsValid(valid);
    }

    const saveOrUpdateBtn = (
        <Button
            variant="submit"
            size="medium"
            disabled={!isValid || isModalLoading}
        >
            Save Changes
        </Button>
    );
    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.target;

        const fakeEvent = {
            target: {
                id: { value: form.id.value },
                dcgId: { value: form.dcgId.value },
                tenantName: { value: form.tenantName.value.trim() },
                portalAdminName: { value: form.portalAdminName.value.trim() },
                portalAdminEmail: { value: form.portalAdminEmail.value.trim() },
                address1: { value: form.address1.value.trim() },
                address2: { value: form.address2.value.trim() },
                city: { value: form.city.value.trim() },
                state: { value: form.state.value },
                zip: { value: form.zip.value.trim() },
                phone: { value: form.phone.value.trim() },
                country: { value: tenantCountry }
            }
        };

        setIsModalLoading(true);
        try {
            await submitHandler(fakeEvent);
        } catch (err) {
            console.error("Submit failed", err);
            setIsErrored(true);
        } finally {
            setIsModalLoading(false);
        }
    };
    const stateSectionOptions = tenantCountry === countries.Canada ? provinces : states;
    const stateLabel = tenantCountry === countries.Canada ? "Province *" : "State *";
        


    return (
        <Modal size="md" isOpen={isOpen}>
            <PreventEnterSubmitForm onSubmit={handleSubmit}>
                <ActionModalHeader
                    title="Edit HQ Info"
                    onCancel={cancel}
                    cancelButtonDisabled={isModalLoading}
                    actionButton={saveOrUpdateBtn}
                />
                <ModalBody>
                    {isModalLoading && <Spinner />}
                    {!isModalLoading && isErrored && (
                        <p>Error loading HQ data. Error Message: {getError}</p>
                    )}
                    {!isModalLoading && !isErrored && (
                        <Col className="justify-content-center">
                            <input type="hidden" id="dcgId" value={data.dcgOrgId} />
                            <input type="hidden" id="id" value={accountId} />

                            {[
                                { id: "tenantName", label: "HQ Name*", value: tenantName, onChange: setTenantName, required: true, maxLength: 100, minLength: 1 },
                                { id: "hqId", label: "HQ Account Id", value: `HQ-${data.tenantAccountId}`, disabled: true },
                                { id: "portalAdminName", label: "Portal Admin Name", value: data.portalAdminName, disabled: true, minLength: 1 },
                                { id: "portalAdminEmail", label: "Admin Email", value: data.portalAdminEmail, disabled: true },
                                { id: "country", label: "Country", value: data.country === 2 ? "Canada" : "United States", disabled: true },
                                { id: "address1", label: "Address 1*", value: address1, onChange: setAddress1, required: true, minLength: 1 },
                                { id: "address2", label: "Address 2", value: address2, onChange: setAddress2 },
                                { id: "city", label: "City*", value: city, onChange: setCity, required: true, minLength: 1 },
                            ].map((f) => (
                                <FormGroup row key={f.id} className="mb-3">
                                    <Label for={f.id} sm={4} className="col-form-label-sm label-md">
                                        {f.label}
                                    </Label>
                                    <Col sm={8}>
                                        <Input
                                            id={f.id}
                                            value={f.value}
                                            onChange={(e) => f.onChange?.(e.target.value.trimStart())}
                                            disabled={f.disabled}
                                            required={f.required}
                                            maxLength={f.maxLength}
                                            minLength={f.minLength}
                                            bsSize="sm"
                                        />
                                    </Col>
                                </FormGroup>
                            ))}

                            <FormGroup row className="mb-3">
                                <Label for="state" sm={4} className="col-form-label-sm label-md">
                                    {stateLabel}
                                </Label>
                                <Col sm={8}>
                                    <BootstrapSelect
                                        id="state"
                                        name="state"
                                        required
                                        options={stateSectionOptions}
                                        value={stateSectionOptions.find((opt) => opt.value === addressState)}
                                        onChange={(opt) => setAddressState(opt.value)}
                                        size="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-3">
                                <Label for="zip" sm={4} className="col-form-label-sm label-md">Postal Code*</Label>
                                <Col sm={8}>
                                    <PostalZipCodeControl id="zip"
                                        required={true}
                                        defaultValue={zipValue}
                                        onChange={handleZipChange}
                                        valid={!zipError} 
                                        minLength={5}
                                        region={tenantCountry}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-2">
                                <Label for="phone" sm={4} className="col-form-label-sm label-md">
                                    Phone #*
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        id="phone"
                                        value={phoneValue}
                                        onChange={handlePhoneChange}
                                        onBlur={handlePhoneBlur}
                                        required={true}
                                        min={10}
                                        valid={!phoneError}
                                        invalid={phoneError}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>
                        </Col>
                    )}
                </ModalBody>
            </PreventEnterSubmitForm>
        </Modal>
    );
}

EditTenantModal.propTypes = {
    isOpen: PropTypes.bool,
    cancel: PropTypes.func,
    accountId: PropTypes.string,
    submitHandler: PropTypes.func,
};

export default EditTenantModal;
